'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import type { SearchResult } from '@/lib/search-index';
import { trackInternalSearch, trackInternalSearchClick } from '@/lib/analytics';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface BlogPostSearchResult {
  slug: string;
  title: string;
  description?: string;
  excerpt?: string;
  categories?: string[];
  tags?: string[];
}

interface APISearchResponse {
  query: string;
  count: number;
  results: SearchResult[];
}

interface BlogSearchResponse {
  query: string;
  count: number;
  posts: BlogPostSearchResult[];
}

// Extended type to support instant actions and deep links
type ExtendedSearchResult = SearchResult & {
  isInstant?: boolean;
  instantValue?: string;
  instantAction?: 'copy';
  isRecent?: boolean;
};

const typeColors: Record<SearchResult['type'] | 'instant', string> = {
  service: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  industry: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
  'case-study': 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  'knowledge-base': 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
  blog: 'bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-300',
  glossary: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300',
  tool: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
  instant: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
};

const typeLabels: Record<SearchResult['type'] | 'instant', string> = {
  service: 'Service',
  industry: 'Industry',
  'case-study': 'Case Study',
  'knowledge-base': 'Guide',
  blog: 'Blog',
  glossary: 'Glossary',
  tool: 'Tool',
  instant: 'Instant',
};

// Recent history localStorage key
const RECENT_TOOLS_KEY = 'inventive_recent_tools';
const MAX_RECENT_TOOLS = 5;

// Helper to generate UUID v4
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// ── Instant-result helpers ──────────────────────────────────────────────
const IP_QUERIES = ['ip', 'my ip', 'what is my ip', 'myip', "what's my ip", 'whats my ip', 'my ip address'];

const LOREM_IPSUM =
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.';

// Cryptographically strong password (Crockford-ish set, no ambiguous chars)
function generatePassword(len = 20): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*-_=+';
  const out: string[] = [];
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const arr = new Uint32Array(len);
    crypto.getRandomValues(arr);
    for (let i = 0; i < len; i++) out.push(chars[arr[i] % chars.length]);
  } else {
    for (let i = 0; i < len; i++) out.push(chars[Math.floor(Math.random() * chars.length)]);
  }
  return out.join('');
}

// Unicode-safe base64
function safeBtoa(s: string): string {
  try { return btoa(unescape(encodeURIComponent(s))); } catch { return ''; }
}
function safeAtob(s: string): string {
  try { return decodeURIComponent(escape(atob(s))); } catch { return ''; }
}

// "<verb> <text>" transforms that compute a copyable answer inline.
const INSTANT_TRANSFORMS: { prefixes: string[]; id: string; label: string; fn: (s: string) => string }[] = [
  { prefixes: ['base64 encode ', 'b64 encode ', 'encode base64 '], id: 'b64enc', label: 'Base64 encoded', fn: (s) => safeBtoa(s) },
  { prefixes: ['base64 decode ', 'b64 decode ', 'decode base64 '], id: 'b64dec', label: 'Base64 decoded', fn: (s) => safeAtob(s) },
  { prefixes: ['url encode ', 'urlencode '], id: 'urlenc', label: 'URL encoded', fn: (s) => { try { return encodeURIComponent(s); } catch { return ''; } } },
  { prefixes: ['url decode ', 'urldecode '], id: 'urldec', label: 'URL decoded', fn: (s) => { try { return decodeURIComponent(s); } catch { return ''; } } },
  { prefixes: ['uppercase ', 'upper '], id: 'upper', label: 'Uppercase', fn: (s) => s.toUpperCase() },
  { prefixes: ['lowercase ', 'lower '], id: 'lower', label: 'Lowercase', fn: (s) => s.toLowerCase() },
  { prefixes: ['reverse '], id: 'reverse', label: 'Reversed', fn: (s) => s.split('').reverse().join('') },
  { prefixes: ['slugify ', 'slug '], id: 'slug', label: 'Slug', fn: (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') },
  { prefixes: ['word count ', 'count words '], id: 'wc', label: 'Word count', fn: (s) => String(s.trim().split(/\s+/).filter(Boolean).length) },
  { prefixes: ['char count ', 'count chars ', 'character count '], id: 'cc', label: 'Character count', fn: (s) => String(s.length) },
];

// Deep link mappings for tool-specific queries
interface DeepLinkMapping {
  keywords: string[];
  url: string;
  title: string;
  description: string;
}

const DEEP_LINK_MAPPINGS: DeepLinkMapping[] = [
  // RAID simulator deep link (also keeps the tool discoverable while the D1 index catches up)
  {
    keywords: ['raid', 'raid sim', 'raid simulator', 'disk array simulator', 'virtual raid'],
    url: '/tools/developer/raid-simulator',
    title: 'RAID Simulator',
    description: 'Build virtual RAID arrays, route payloads, inject failures, and watch rebuilds and RAID conversions',
  },
  // Base64 deep links
  {
    keywords: ['decode base64', 'base64 decode', 'base64 decoder'],
    url: '/tools/base64-encoder-decoder?mode=decode',
    title: 'Decode Base64',
    description: 'Decode Base64 encoded strings back to plain text',
  },
  {
    keywords: ['encode base64', 'base64 encode', 'base64 encoder'],
    url: '/tools/base64-encoder-decoder?mode=encode',
    title: 'Encode Base64',
    description: 'Encode plain text to Base64 format',
  },
  {
    keywords: ['base64 url safe', 'urlsafe base64'],
    url: '/tools/base64-encoder-decoder?mode=encode&encoding=urlsafe',
    title: 'URL-Safe Base64 Encoding',
    description: 'Encode text to URL-safe Base64 format',
  },
  // JSON Formatter deep links
  {
    keywords: ['minify json', 'json minify', 'compress json', 'json compress'],
    url: '/tools/json-formatter?minify=true',
    title: 'Minify JSON',
    description: 'Compress JSON by removing whitespace and formatting',
  },
  {
    keywords: ['beautify json', 'format json', 'json beautify', 'pretty json', 'json pretty'],
    url: '/tools/json-formatter?indent=2',
    title: 'Beautify JSON',
    description: 'Format and beautify JSON with proper indentation',
  },
  {
    keywords: ['sort json keys', 'json sort', 'alphabetize json'],
    url: '/tools/json-formatter?sort=true',
    title: 'Sort JSON Keys',
    description: 'Format JSON with alphabetically sorted keys',
  },
  {
    keywords: ['json tree', 'json tree view', 'visualize json'],
    url: '/tools/json-formatter?view=tree',
    title: 'JSON Tree View',
    description: 'Visualize JSON structure in an interactive tree',
  },
  // Hash generators
  {
    keywords: ['md5 hash', 'generate md5'],
    url: '/tools/hash-generator?algorithm=md5',
    title: 'Generate MD5 Hash',
    description: 'Generate MD5 hash from text input',
  },
  {
    keywords: ['sha256 hash', 'generate sha256', 'sha-256'],
    url: '/tools/hash-generator?algorithm=sha256',
    title: 'Generate SHA-256 Hash',
    description: 'Generate SHA-256 hash from text input',
  },
  {
    keywords: ['sha1 hash', 'generate sha1', 'sha-1'],
    url: '/tools/hash-generator?algorithm=sha1',
    title: 'Generate SHA-1 Hash',
    description: 'Generate SHA-1 hash from text input',
  },
];

// Curated list of popular searches (static to avoid API call on modal open)
const POPULAR_SEARCHES: SearchResult[] = [
  {
    id: 'service-vciso',
    title: 'vCISO',
    description: 'Virtual Chief Information Security Officer services for strategic security leadership',
    url: '/services/vciso',
    type: 'service',
    category: 'Leadership',
  },
  {
    id: 'tool-password-strength-checker',
    title: 'Password Strength Checker',
    description: 'Test your password strength and get recommendations for improvement',
    url: '/tools/security/password-strength-checker',
    type: 'tool',
    category: 'Security',
  },
  {
    id: 'service-security-awareness',
    title: 'Security Awareness Training',
    description: 'Employee security awareness and phishing simulation programs',
    url: '/services/security-awareness',
    type: 'service',
    category: 'Training',
  },
  {
    id: 'tool-subnet-calculator',
    title: 'Subnet Calculator',
    description: 'Calculate IP subnets, CIDR ranges, and network addresses',
    url: '/tools/network/subnet-calculator',
    type: 'tool',
    category: 'Network',
  },
  {
    id: 'industry-healthcare',
    title: 'Healthcare',
    description: 'HIPAA compliance and healthcare cybersecurity solutions',
    url: '/industries/healthcare',
    type: 'industry',
  },
  {
    id: 'glossary-zero-trust',
    title: 'Zero Trust Architecture',
    description: 'A security model requiring continuous verification of every user and device',
    url: '/glossary/zero-trust-architecture',
    type: 'glossary',
    category: 'Security Foundations',
  },
];

// Get recent tools from localStorage
function getRecentTools(): ExtendedSearchResult[] {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem(RECENT_TOOLS_KEY);
    if (!stored) return [];
    const recent = JSON.parse(stored) as ExtendedSearchResult[];
    return recent.map((r) => ({ ...r, isRecent: true }));
  } catch {
    return [];
  }
}

// Save a tool to recent history
function saveToRecentTools(result: SearchResult): void {
  if (typeof window === 'undefined') return;
  if (result.type !== 'tool') return; // Only save tools

  try {
    const stored = localStorage.getItem(RECENT_TOOLS_KEY);
    let recent: SearchResult[] = stored ? JSON.parse(stored) : [];

    // Remove if already exists (to move to front)
    recent = recent.filter((r) => r.url !== result.url);

    // Add to front
    recent.unshift({
      id: result.id,
      title: result.title,
      description: result.description,
      url: result.url,
      type: result.type,
      category: result.category,
    });

    // Keep only MAX_RECENT_TOOLS
    recent = recent.slice(0, MAX_RECENT_TOOLS);

    localStorage.setItem(RECENT_TOOLS_KEY, JSON.stringify(recent));
  } catch {
    // Silently fail if localStorage is not available
  }
}

export default function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ExtendedSearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recentTools, setRecentTools] = useState<ExtendedSearchResult[]>([]);
  const [copiedInstant, setCopiedInstant] = useState<string | null>(null);
  const [userIP, setUserIP] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useFocusTrap(modalRef, isOpen);

  // Load recent tools on mount
  useEffect(() => {
    if (isOpen) {
      setRecentTools(getRecentTools());
    }
  }, [isOpen]);

  // Fetch user IP when searching for it
  useEffect(() => {
    const lowerQuery = query.toLowerCase().trim();
    if (IP_QUERIES.includes(lowerQuery) && !userIP) {
      fetch('https://api.ipify.org?format=json')
        .then((res) => res.json())
        .then((data) => setUserIP(data.ip))
        .catch(() => setUserIP(null));
    }
  }, [query, userIP]);

  // Check for instant results based on query
  const getInstantResults = useCallback((searchQuery: string): ExtendedSearchResult[] => {
    const raw = searchQuery.trim();
    const lowerQuery = raw.toLowerCase();
    const out: ExtendedSearchResult[] = [];
    const add = (id: string, title: string, description: string, value: string) =>
      out.push({ id, title, description, url: '#', type: 'tool', isInstant: true, instantValue: value, instantAction: 'copy' });

    // UUID
    if (['uuid', 'generate uuid', 'new uuid', 'guid'].includes(lowerQuery)) {
      const uuid = generateUUID();
      add('instant-uuid', `UUID: ${uuid}`, 'Click to copy this generated UUID', uuid);
    }

    // Public IP (fetched asynchronously into userIP)
    if (IP_QUERIES.includes(lowerQuery)) {
      if (userIP) add('instant-ip', `Your IP: ${userIP}`, 'Click to copy your public IP address', userIP);
      else out.push({ id: 'instant-ip-loading', title: 'Your IP: Loading...', description: 'Fetching your public IP address', url: '#', type: 'tool', isInstant: true });
    }

    // Strong password
    if (['password', 'generate password', 'strong password', 'random password', 'new password'].includes(lowerQuery)) {
      const pw = generatePassword(20);
      add('instant-password', `Password: ${pw}`, 'A strong random 20-character password — click to copy', pw);
    }

    // Lorem ipsum
    if (['lorem ipsum', 'lorem', 'placeholder text', 'dummy text', 'filler text'].includes(lowerQuery)) {
      add('instant-lorem', 'Lorem ipsum (1 paragraph)', `${LOREM_IPSUM.slice(0, 70)}… — click to copy a full paragraph`, LOREM_IPSUM);
    }

    // Unix timestamp (now)
    if (['timestamp', 'unix timestamp', 'unix time', 'epoch', 'epoch time', 'current timestamp'].includes(lowerQuery)) {
      const ts = Math.floor(Date.now() / 1000).toString();
      add('instant-timestamp', `Unix timestamp: ${ts}`, 'Current Unix epoch in seconds — click to copy', ts);
    }

    // Current date/time (ISO 8601)
    if (['now', 'current time', 'current date', 'iso date', 'datetime', 'date time'].includes(lowerQuery)) {
      const iso = new Date().toISOString();
      add('instant-now', `Now: ${iso}`, 'Current date/time in ISO 8601 (UTC) — click to copy', iso);
    }

    // Random number
    if (['random number', 'random', 'random int'].includes(lowerQuery)) {
      const n = (Math.floor(Math.random() * 100) + 1).toString();
      add('instant-rand', `Random number: ${n}`, 'A random number from 1 to 100 — click to copy', n);
    }

    // Random hex color
    if (['random color', 'random hex', 'hex color', 'color code'].includes(lowerQuery)) {
      const hex = '#' + Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0');
      add('instant-color', `Color: ${hex}`, 'A random hex color — click to copy', hex);
    }

    // User agent
    if (['user agent', 'my user agent', 'useragent', 'what is my user agent', 'my useragent'].includes(lowerQuery)) {
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
      if (ua) add('instant-ua', 'Your user agent', `${ua.slice(0, 70)}… — click to copy`, ua);
    }

    // Inline transforms: "<verb> <text>" (base64, url encode, case, reverse, slugify, counts)
    for (const t of INSTANT_TRANSFORMS) {
      const pfx = t.prefixes.find((p) => lowerQuery.startsWith(p));
      if (!pfx) continue;
      const input = raw.slice(pfx.length).trim();
      if (!input) continue;
      const result = t.fn(input);
      if (!result) continue;
      const shownOut = result.length > 60 ? `${result.slice(0, 60)}…` : result;
      const shownIn = input.length > 30 ? `${input.slice(0, 30)}…` : input;
      add(`instant-${t.id}`, `${t.label}: ${shownOut}`, `${t.label} of "${shownIn}" — click to copy`, result);
    }

    return out;
  }, [userIP]);

  // Check for deep link matches
  const getDeepLinkResults = useCallback((searchQuery: string): ExtendedSearchResult[] => {
    const lowerQuery = searchQuery.toLowerCase().trim();
    const deepLinkResults: ExtendedSearchResult[] = [];

    for (const mapping of DEEP_LINK_MAPPINGS) {
      if (mapping.keywords.some((kw) => lowerQuery.includes(kw) || kw.includes(lowerQuery))) {
        deepLinkResults.push({
          id: `deeplink-${mapping.url}`,
          title: mapping.title,
          description: mapping.description,
          url: mapping.url,
          type: 'tool',
        });
      }
    }

    return deepLinkResults;
  }, []);

  // Handle search with debounce
  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      setSelectedIndex(0);
      setError(null);
      return;
    }

    // Check for instant results immediately (no debounce)
    const instantResults = getInstantResults(query);
    const deepLinkResults = getDeepLinkResults(query);

    // If we have instant results, show them immediately
    if (instantResults.length > 0 || deepLinkResults.length > 0) {
      setResults([...instantResults, ...deepLinkResults]);
      setSelectedIndex(0);
    }

    setIsSearching(true);
    setError(null);

    const timer = setTimeout(async () => {
      try {
        // Fetch from both search APIs in parallel
        const [searchResponse, blogResponse] = await Promise.allSettled([
          fetch(`/api/search?q=${encodeURIComponent(query)}&limit=15`),
          fetch(`/api/blog/search?q=${encodeURIComponent(query)}`),
        ]);

        let allResults: ExtendedSearchResult[] = [];

        // Handle main search API results (glossary, tools, etc.)
        if (searchResponse.status === 'fulfilled' && searchResponse.value.ok) {
          const data: APISearchResponse = await searchResponse.value.json();
          allResults = [...allResults, ...data.results];
        }

        // Handle blog search results
        if (blogResponse.status === 'fulfilled' && blogResponse.value.ok) {
          const blogData: BlogSearchResponse = await blogResponse.value.json();
          const blogResults: ExtendedSearchResult[] = (blogData.posts || []).slice(0, 5).map((post) => ({
            id: `blog-${post.slug}`,
            title: post.title,
            description: post.description || post.excerpt || '',
            url: `/blog/${post.slug}`,
            type: 'blog' as const,
            category: post.categories?.[0] || undefined,
            tags: post.tags || [],
          }));
          allResults = [...allResults, ...blogResults];
        }

        // Add instant results at the top
        const currentInstantResults = getInstantResults(query);
        const currentDeepLinkResults = getDeepLinkResults(query);

        // Deduplicate by id (including deep links that might match regular results)
        const seen = new Set<string>();
        // Add instant and deep link IDs first
        currentInstantResults.forEach((r) => seen.add(r.id));
        currentDeepLinkResults.forEach((r) => seen.add(r.id));
        // Also mark deep link URLs as seen to avoid duplicates from API
        currentDeepLinkResults.forEach((r) => seen.add(r.url));

        const uniqueApiResults = allResults.filter((result) => {
          if (seen.has(result.id)) return false;
          if (seen.has(result.url)) return false;
          seen.add(result.id);
          return true;
        });

        // Sort API results by relevance - put tools and glossary first when searching
        uniqueApiResults.sort((a, b) => {
          const priority: Record<string, number> = {
            tool: 1,
            glossary: 2,
            service: 3,
            blog: 4,
            industry: 5,
            'case-study': 6,
            'knowledge-base': 7,
          };
          return (priority[a.type] || 10) - (priority[b.type] || 10);
        });

        // Combine: instant results first, then deep links, then API results
        const combinedResults = [
          ...currentInstantResults,
          ...currentDeepLinkResults,
          ...uniqueApiResults.slice(0, 15 - currentInstantResults.length - currentDeepLinkResults.length),
        ];

        setResults(combinedResults);

        // Track search with results count
        trackInternalSearch(query, combinedResults.length, 'command_palette');

        // Show error if no results from either API
        if (combinedResults.length === 0 && searchResponse.status === 'rejected' && blogResponse.status === 'rejected') {
          setError('Search unavailable. Please try again later.');
        }
      } catch (err) {
        console.error('Search error:', err);
        setError('Search failed. Please try again.');
        // Keep instant results if we have them
        const currentInstantResults = getInstantResults(query);
        const currentDeepLinkResults = getDeepLinkResults(query);
        if (currentInstantResults.length > 0 || currentDeepLinkResults.length > 0) {
          setResults([...currentInstantResults, ...currentDeepLinkResults]);
        } else {
          setResults([]);
        }
      }

      setSelectedIndex(0);
      setIsSearching(false);
    }, 200);

    return () => clearTimeout(timer);
  }, [query, getInstantResults, getDeepLinkResults]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle instant action (copy to clipboard)
  const handleInstantAction = useCallback(async (result: ExtendedSearchResult) => {
    if (result.instantAction === 'copy' && result.instantValue) {
      try {
        await navigator.clipboard.writeText(result.instantValue);
        setCopiedInstant(result.id);
        setTimeout(() => setCopiedInstant(null), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  }, []);

  // Handle result selection (navigation or instant action)
  const handleResultSelect = useCallback((result: ExtendedSearchResult, index?: number) => {
    if (result.isInstant && result.instantAction) {
      handleInstantAction(result);
      return;
    }

    // Track search result click
    if (query.trim()) {
      trackInternalSearchClick(
        query,
        result.url,
        index !== undefined ? index + 1 : selectedIndex + 1,
        result.type as 'tool' | 'blog' | 'service' | 'kb' | 'glossary'
      );
    }

    // Save to recent history if it's a tool
    if (result.type === 'tool' && !result.isInstant) {
      saveToRecentTools(result);
    }

    onClose();
    window.location.href = result.url;
  }, [handleInstantAction, onClose, query, selectedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      // Calculate what results are currently being displayed
      const displayedResults = results.length > 0
        ? results
        : query.length === 0
          ? [...(recentTools.length > 0 ? recentTools : []), ...POPULAR_SEARCHES]
          : [];

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < displayedResults.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case 'Enter':
          e.preventDefault();
          const selectedResult = displayedResults[selectedIndex];
          if (selectedResult) {
            handleResultSelect(selectedResult);
          }
          break;
      }
    },
    [isOpen, results, selectedIndex, onClose, query, recentTools, handleResultSelect]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current) {
      const selectedElement = resultsRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  if (!isOpen) return null;

  // Build display results with recent tools section
  const displayResults: ExtendedSearchResult[] = results.length > 0
    ? results
    : query.length === 0
      ? [...(recentTools.length > 0 ? recentTools : []), ...POPULAR_SEARCHES]
      : [];

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

      {/* Modal */}
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label="Search" className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[600px] flex flex-col">
        {/* Search Input */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              ref={inputRef}
              type="text"
              aria-label="Search services, industries, tools, guides, and blog posts"
              placeholder="Search services, industries, tools, guides, blog posts..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 text-lg outline-none focus:ring-2 focus:ring-[#2813e8] text-gray-900 dark:text-gray-100 placeholder-gray-400"
            />
            {isSearching && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
            )}
            <button
              onClick={onClose}
              className="inline-flex items-center justify-center min-h-[24px] min-w-[24px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-400 text-sm font-medium"
            >
              ESC
            </button>
          </div>
        </div>

        {/* Results */}
        <div ref={resultsRef} className="flex-1 overflow-y-auto" aria-live="polite" aria-atomic="true">
          {error ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
              <svg
                className="w-16 h-16 mb-4 text-red-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="text-lg font-medium">{error}</p>
            </div>
          ) : displayResults.length > 0 ? (
            <div className="py-2">
              {/* Recent Tools Section */}
              {query.length === 0 && recentTools.length > 0 && (
                <>
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Recent Tools
                  </div>
                  {recentTools.map((result, index) => (
                    <button
                      key={`recent-${result.id}`}
                      onClick={() => handleResultSelect(result)}
                      className={`w-full text-left block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                        index === selectedIndex ? 'bg-blue-50 dark:bg-blue-950/40' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-900 dark:text-gray-100 truncate">{result.title}</span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeColors[result.type]}`}>
                              {typeLabels[result.type]}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{result.description}</p>
                        </div>
                        <svg className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  ))}
                  <div className="border-t border-gray-100 dark:border-gray-800 my-2" />
                </>
              )}

              {/* Popular Searches Header */}
              {query.length === 0 && (
                <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Popular Searches
                </div>
              )}

              {/* Instant Results indicator */}
              {query.length > 0 && results.some((r) => r.isInstant) && (
                <div className="px-4 py-2 text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Instant Results
                </div>
              )}

              {/* Results List */}
              {displayResults.map((result, index) => {
                // Skip recent tools in the main list (they're shown above)
                if (query.length === 0 && result.isRecent) return null;

                const adjustedIndex = query.length === 0 && recentTools.length > 0
                  ? index
                  : index;

                // Render instant result (copyable)
                if (result.isInstant) {
                  return (
                    <button
                      key={result.id}
                      onClick={() => handleInstantAction(result)}
                      className={`w-full text-left block px-4 py-3 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 transition-colors ${
                        adjustedIndex === selectedIndex ? 'bg-emerald-50 dark:bg-emerald-950/40' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-900 dark:text-gray-100 truncate font-mono">
                              {result.title}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeColors.instant}`}>
                              {copiedInstant === result.id ? 'Copied!' : 'Click to Copy'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{result.description}</p>
                        </div>
                        <svg
                          className={`w-5 h-5 flex-shrink-0 mt-1 ${copiedInstant === result.id ? 'text-emerald-500' : 'text-gray-400'}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          {copiedInstant === result.id ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                          )}
                        </svg>
                      </div>
                    </button>
                  );
                }

                // Render regular result (navigable)
                return (
                  <Link
                    key={result.id}
                    href={result.url}
                    onClick={() => {
                      // Track search result click
                      if (query.trim()) {
                        trackInternalSearchClick(
                          query,
                          result.url,
                          index + 1,
                          result.type as 'tool' | 'blog' | 'service' | 'kb' | 'glossary'
                        );
                      }
                      if (result.type === 'tool') {
                        saveToRecentTools(result);
                      }
                      onClose();
                    }}
                    className={`block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                      adjustedIndex === selectedIndex ? 'bg-blue-50 dark:bg-blue-950/40' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900 dark:text-gray-100 truncate">{result.title}</span>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              typeColors[result.type]
                            }`}
                          >
                            {typeLabels[result.type]}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{result.description}</p>
                        {result.category && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Category: {result.category}</p>
                        )}
                      </div>
                      <svg
                        className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : query.length >= 2 && !isSearching ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
              <svg
                className="w-16 h-16 mb-4 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-lg font-medium">No results found</p>
              <p className="text-sm mt-1">Try searching for services, industries, tools, or blog topics</p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
              <svg
                className="w-16 h-16 mb-4 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <p className="text-lg font-medium">Start typing to search</p>
              <p className="text-sm mt-1">Search across services, industries, tools, guides, and blog posts</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-b-xl">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-2 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-xs">↑</kbd>
                <kbd className="px-2 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-xs">↓</kbd>
                <span>Navigate</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-2 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-xs">Enter</kbd>
                <span>Select</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-2 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-xs">ESC</kbd>
                <span>Close</span>
              </span>
            </div>
            <span>{displayResults.length} results</span>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
